export enum ThemeMode {
  LIGHT = 'light',
  DARK = 'dark',
  AUTO = 'auto',
}

const THEME_STORAGE_KEY = 'multidimensional-notes-theme'

const THEME_SEQUENCE: Record<ThemeMode, ThemeMode> = {
  [ThemeMode.LIGHT]: ThemeMode.DARK,
  [ThemeMode.DARK]: ThemeMode.AUTO,
  [ThemeMode.AUTO]: ThemeMode.LIGHT,
}

export const getSystemThemePreference = (): boolean => {
  if (typeof window === 'undefined' || !window.matchMedia) return false
  return window.matchMedia('(prefers-color-scheme: dark)').matches
}

export const isDarkTheme = (theme: ThemeMode): boolean => {
  return theme === ThemeMode.DARK || (theme === ThemeMode.AUTO && getSystemThemePreference())
}

export const getNextTheme = (theme: ThemeMode): ThemeMode => {
  return THEME_SEQUENCE[theme] ?? ThemeMode.LIGHT
}

export const getStoredTheme = (): ThemeMode => {
  if (typeof window === 'undefined') return ThemeMode.LIGHT

  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY)
    if (stored && Object.values(ThemeMode).includes(stored as ThemeMode)) {
      return stored as ThemeMode
    }
  } catch {
    // WHY: 隐私模式或禁用存储时不应阻断应用启动，直接回退默认主题即可。
  }

  return ThemeMode.LIGHT
}

export const saveThemeToStorage = (theme: ThemeMode): void => {
  if (typeof window === 'undefined') return

  try {
    localStorage.setItem(THEME_STORAGE_KEY, theme)
  } catch {
    // WHY: 主题持久化失败不影响当前会话的视觉状态，静默降级比打断交互更合适。
  }
}

export const applyThemeToDocument = (theme: ThemeMode): void => {
  if (typeof document === 'undefined') return

  const root = document.documentElement
  const isDark = isDarkTheme(theme)

  // WHY: 主题变量会同时影响 Ant Design 和 Tailwind 类名，短暂加过渡类能避免整页瞬时闪烁。
  root.classList.add('theme-transitioning')

  if (isDark) {
    root.classList.add('dark')
    root.classList.remove('light')
  } else {
    root.classList.add('light')
    root.classList.remove('dark')
  }

  setTimeout(() => {
    root.classList.remove('theme-transitioning')
  }, 300)
}

export const subscribeSystemThemeChange = (
  theme: ThemeMode,
  onAutoThemeChange: () => void
): (() => void) => {
  if (typeof window === 'undefined' || !window.matchMedia) {
    return () => undefined
  }

  const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
  const handleSystemThemeChange = () => {
    if (theme === ThemeMode.AUTO) {
      onAutoThemeChange()
    }
  }

  // WHY: 旧版 WebKit 只支持 addListener，保留兜底能避免自动主题在旧浏览器中失效。
  if (mediaQuery.addEventListener) {
    mediaQuery.addEventListener('change', handleSystemThemeChange)
    return () => mediaQuery.removeEventListener('change', handleSystemThemeChange)
  }

  mediaQuery.addListener(handleSystemThemeChange)
  return () => mediaQuery.removeListener(handleSystemThemeChange)
}
